import { object, string, number } from 'yup';
import CycleSettings from '../enums/cycle-setting.enum';
import { POST_WELD_SETTINGS_LIMITS } from '../components/PostWeldSettings/constants';
import { PREHEAT_SETTINGS_LIMITS } from '../components/PreHeatSettings/constants';
import { PERCENTAGE_TIMER_SETTINGS_LIMITS } from '../components/PercentageTimerSettings/constants';
import CycleMeasurement from '../enums/cycle-measurement';
import { convertAbsoluteTemperature, convertTemperatureDelta } from './temperature-unit-conversion';
import { StepMode } from '../types/recipes-types';
import { CycleSettingsFormValues } from '../components/CycleSettingsInput/CycleSettingsInput';

const getTemperatureLimit = (
  celsiusValue: number,
  measurement: CycleMeasurement,
  isAbsoluteTemperature = false,
): number => {
  const converted = isAbsoluteTemperature
    ? convertAbsoluteTemperature(celsiusValue, CycleMeasurement.Celsius, measurement)
    : convertTemperatureDelta(celsiusValue, CycleMeasurement.Celsius, measurement);

  return Number(converted);
};

// A blank tolerance input reaches here as '' (see CycleSettingsInput's
// handleFieldChange), which yup's number() casts to NaN - treat that (and a
// genuinely missing value) as 0 rather than a validation failure.
const toleranceTransform = (value: number, originalValue: unknown) =>
  originalValue === '' || originalValue === null || originalValue === undefined || Number.isNaN(value) ? 0 : value;

const TOLERANCE_FIELDS = [
  'soakUpper',
  'soakLower',
  'rateUpperTolerance',
  'rateLowerTolerance',
  'holdUpperTolerance',
  'holdLowerTolerance',
] as const;

// Applies the same "blank means 0" substitution to the payload actually
// submitted for cycle creation/update - the validation schema's transform
// above only affects what yup considers valid, not the values formik holds.
export const applyDefaultTolerances = (settings: CycleSettingsFormValues): CycleSettingsFormValues => {
  if (!settings) {
    return settings;
  }

  const normalized = { ...settings };

  TOLERANCE_FIELDS.forEach((key) => {
    const value = normalized[key];

    if (value === '' || value === null || value === undefined || Number.isNaN(value as number)) {
      normalized[key] = 0;
    }
  });

  return normalized;
};

export const createCycleSettingsValidationSchema = (measurement: CycleMeasurement) => {
  const preHeatRateMin = getTemperatureLimit(PREHEAT_SETTINGS_LIMITS.RATE.MIN, measurement);
  const preHeatRateMax = getTemperatureLimit(PREHEAT_SETTINGS_LIMITS.RATE.MAX, measurement);
  const finalTempMin = getTemperatureLimit(PREHEAT_SETTINGS_LIMITS.FINAL_TEMPERATURE.MIN, measurement, true);
  const finalTempMax = getTemperatureLimit(PREHEAT_SETTINGS_LIMITS.FINAL_TEMPERATURE.MAX, measurement, true);
  const soakUpperMin = getTemperatureLimit(PREHEAT_SETTINGS_LIMITS.UPPER_HOLD_TOLERANCE.MIN, measurement);
  const soakUpperMax = getTemperatureLimit(PREHEAT_SETTINGS_LIMITS.UPPER_HOLD_TOLERANCE.MAX, measurement);
  const soakLowerMin = getTemperatureLimit(PREHEAT_SETTINGS_LIMITS.LOWER_HOLD_TOLERANCE.MIN, measurement);
  const soakLowerMax = getTemperatureLimit(PREHEAT_SETTINGS_LIMITS.LOWER_HOLD_TOLERANCE.MAX, measurement);
  const rateUpperToleranceMin = getTemperatureLimit(POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MIN, measurement);
  const rateUpperToleranceMax = getTemperatureLimit(POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MAX, measurement);
  const rateLowerToleranceMin = getTemperatureLimit(POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MIN, measurement);
  const rateLowerToleranceMax = getTemperatureLimit(POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MAX, measurement);
  const holdUpperToleranceMin = getTemperatureLimit(POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MIN, measurement);
  const holdUpperToleranceMax = getTemperatureLimit(POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MAX, measurement);
  const holdLowerToleranceMin = getTemperatureLimit(POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MIN, measurement);
  const holdLowerToleranceMax = getTemperatureLimit(POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MAX, measurement);

  return object({
    mode: string().nullable(),
    rate: number().when('mode', {
      is: CycleSettings.PreHeat,
      then: (schema) =>
        schema
          .required('Rate is required for Pre-Heat')
          .min(preHeatRateMin, `Rate must be at least ${preHeatRateMin}`)
          .max(preHeatRateMax, `Rate must be at most ${preHeatRateMax}`),
    }),
    finalTemp: number().when('mode', {
      is: CycleSettings.PreHeat,
      then: (schema) =>
        schema
          .required('Final temperature is required for Pre-Heat')
          .min(finalTempMin, `Final temperature must be at least ${finalTempMin}`)
          .max(finalTempMax, `Final temperature must be at most ${finalTempMax}`),
    }),
    // A blank tolerance is valid - it just means "no tolerance", which is
    // treated as 0 (applyDefaultTolerances does the same substitution on the
    // submitted payload, since this transform only affects validation).
    soakUpper: number().when('mode', {
      is: CycleSettings.PreHeat,
      then: (schema) =>
        schema
          .transform(toleranceTransform)
          .min(soakUpperMin, `Upper soak tolerance must be at least ${soakUpperMin}`)
          .max(soakUpperMax, `Upper soak tolerance must be at most ${soakUpperMax}`),
    }),
    soakLower: number().when('mode', {
      is: CycleSettings.PreHeat,
      then: (schema) =>
        schema
          .transform(toleranceTransform)
          .min(soakLowerMin, `Lower soak tolerance must be at least ${soakLowerMin}`)
          .max(soakLowerMax, `Lower soak tolerance must be at most ${soakLowerMax}`),
    }),
    percentage_timer: number().when('mode', {
      is: CycleSettings.PercentageTimer,
      then: (schema) =>
        schema
          .required('Percentage timer is required')
          .min(
            PERCENTAGE_TIMER_SETTINGS_LIMITS.PT.MIN,
            `Percentage timer must be at least ${PERCENTAGE_TIMER_SETTINGS_LIMITS.PT.MIN}`,
          )
          .max(
            PERCENTAGE_TIMER_SETTINGS_LIMITS.PT.MAX,
            `Percentage timer must be at most ${PERCENTAGE_TIMER_SETTINGS_LIMITS.PT.MAX}`,
          ),
    }),
    // A blank tolerance is valid - it just means "no tolerance", which is
    // treated as 0 (applyDefaultTolerances does the same substitution on the
    // submitted payload, since this transform only affects validation).
    rateUpperTolerance: number().when('mode', {
      is: CycleSettings.PostWeld,
      then: (schema) =>
        schema
          .transform(toleranceTransform)
          .min(rateUpperToleranceMin, `Rate upper tolerance must be at least ${rateUpperToleranceMin}`)
          .max(rateUpperToleranceMax, `Rate upper tolerance must be at most ${rateUpperToleranceMax}`),
    }),
    rateLowerTolerance: number().when('mode', {
      is: CycleSettings.PostWeld,
      then: (schema) =>
        schema
          .transform(toleranceTransform)
          .min(rateLowerToleranceMin, `Rate lower tolerance must be at least ${rateLowerToleranceMin}`)
          .max(rateLowerToleranceMax, `Rate lower tolerance must be at most ${rateLowerToleranceMax}`),
    }),
    holdUpperTolerance: number().when('mode', {
      is: CycleSettings.PostWeld,
      then: (schema) =>
        schema
          .transform(toleranceTransform)
          .min(holdUpperToleranceMin, `Hold upper tolerance must be at least ${holdUpperToleranceMin}`)
          .max(holdUpperToleranceMax, `Hold upper tolerance must be at most ${holdUpperToleranceMax}`),
    }),
    holdLowerTolerance: number().when('mode', {
      is: CycleSettings.PostWeld,
      then: (schema) =>
        schema
          .transform(toleranceTransform)
          .min(holdLowerToleranceMin, `Hold lower tolerance must be at least ${holdLowerToleranceMin}`)
          .max(holdLowerToleranceMax, `Hold lower tolerance must be at most ${holdLowerToleranceMax}`),
    }),
    name: string().when('mode', {
      is: CycleSettings.PostWeld,
      then: (schema) =>
        schema
          .required('Recipe name is required for Post Weld')
          .max(18, 'Recipe name cannot exceed 18 characters')
          .matches(/^[a-zA-Z0-9 ]+$/, 'Recipe name can only contain letters, numbers, and spaces'),
    }),
  });
};

export const convertCycleSettingsUnits = (
  settings: CycleSettingsFormValues,
  from: CycleMeasurement,
  to: CycleMeasurement,
  options: { convertSteps?: boolean } = {},
): CycleSettingsFormValues => {
  if (!settings || from === to) {
    if (!settings?.steps || options.convertSteps !== false) {
      return settings;
    }

    return {
      ...settings,
      steps: settings.steps.map((step) => ({
        ...step,
        isCelsius: from === CycleMeasurement.Celsius,
        units: from,
      })),
    };
  }

  const convertedSettings = { ...settings };
  const deltaFields: (keyof CycleSettingsFormValues)[] = [
    'rate',
    'soakUpper',
    'soakLower',
    'rateUpperTolerance',
    'rateLowerTolerance',
    'holdUpperTolerance',
    'holdLowerTolerance',
  ];

  deltaFields.forEach((key) => {
    (convertedSettings as any)[key] = convertTemperatureDelta(convertedSettings[key], from, to) as number | string;
  });

  convertedSettings.finalTemp = convertAbsoluteTemperature(convertedSettings.finalTemp, from, to) as number | string;

  if (convertedSettings.steps && options.convertSteps !== false) {
    convertedSettings.steps = convertedSettings.steps.map((step) => {
      if (step.mode !== StepMode.Rate) {
        return step;
      }

      return {
        ...step,
        heatingRate: convertTemperatureDelta(step.heatingRate, from, to) as number,
        heatingLimit: convertAbsoluteTemperature(step.heatingLimit, from, to) as number,
        isCelsius: to === CycleMeasurement.Celsius,
        units: to,
      };
    });
  } else if (convertedSettings.steps) {
    convertedSettings.steps = convertedSettings.steps.map((step) => ({
      ...step,
      isCelsius: from === CycleMeasurement.Celsius,
      units: from,
    }));
  }

  return convertedSettings;
};
